import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LocationOption from './LocationOption';
// import LocationTooltip from './LocationTooltip'; // Commented for now, tooltip functionality disabled

/**
 * LocationSelect Component
 *
 * Custom dropdown selector for locations with themed styling.
 * Displays location options using LocationOption component.
 * Manages its own open/closed state.
 *
 * State Ownership:
 * - Owns: isOpen (dropdown open/closed state)
 * - Reads: All props from parent
 *
 * @param {Object} props
 * @param {string} props.label - Label text for the select field
 * @param {string} props.value - Currently selected location ID
 * @param {Function} props.onChange - Change handler, receives synthetic event
 * @param {Array} props.locations - Array of location objects with id, name, address
 * @param {'pickup'|'dropoff'} props.type - Type of location (passed to onChange event.target.name)
 * @param {Object} props.theme - Theme object with cn, background, text from useTheme
 */
const LocationSelect = ({ label, value, onChange, locations, type, theme }) => {
  const { t } = useTranslation();
  const { cn, background, text } = theme;
  const [isOpen, setIsOpen] = useState(false);
  // const [tooltipLocation, setTooltipLocation] = useState(null); // Tooltip disabled for now

  /**
   * Handle location selection from dropdown
   * Creates synthetic event to match form onChange signature
  */
  const handleSelect = (locationId) => {
    onChange({ target: { name: type, value: locationId } });
    setIsOpen(false);
  };

  /* Tooltip functionality disabled - uncomment if needed in future
  const handleLocationHover = (location) => {
    if (!isMobile) {
      setTooltipLocation(location);
    }
  };

  const handleLocationClick = (location) => {
    if (isMobile) {
      setTooltipLocation(tooltipLocation === location ? null : location);
    }
  };
  */

  // Find selected location to display
  const selectedLocation = value ? locations.find(l => String(l.id) === String(value)) : null;
  const displayText = selectedLocation
    ? `${selectedLocation.name}: ${selectedLocation.address}`
    : t('selectLocation');

  return (
    <div className="relative">
      <label className={cn("block text-sm font-medium mb-1", text.primary)}>
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full p-3 text-left border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-hidden",
          background.primary,
          "hover:bg-gray-50 dark:hover:bg-gray-700",
          "border-gray-200 dark:border-gray-700"
        )}
      >
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className={cn("block truncate min-w-0 flex-1", text.primary)}>
            {displayText}
          </span>
          <ChevronRight className={cn(`w-4 h-4 shrink-0 transform transition-transform ${isOpen ? 'rotate-90' : ''}`, text.secondary)} />
        </div>
      </button>

      {isOpen && (
        <div className={cn(
          "absolute z-40 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-auto",
          background.primary,
          "border-gray-200 dark:border-gray-700"
        )}>
          {locations.map((location) => (
            <div
              key={location.id}
              onClick={() => handleSelect(location.id)}
              // onMouseEnter={() => handleLocationHover(location)}
              // onMouseLeave={() => handleLocationHover(null)}
              // onTouchStart={() => handleLocationClick(location)}
              className="cursor-pointer hover:font-bold"
            >
              <LocationOption
                type={location.type}
                name={location.name}
                address={location.address}
                t={t}
              />
            </div>
          ))}
        </div>
      )}

      {/* Tooltip disabled for now - uncomment if needed
      {tooltipLocation && (
        <LocationTooltip
          location={tooltipLocation}
          type={type}
          isVisible={true}
          t={t}
        />
      )}
      */}
    </div>
  );
};


export default React.memo(LocationSelect);
